import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { useToast } from '@ntconsult/aluma-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { campanhasQueries, convitesQueries, painelQueries, sincronizacaoQueries } from '../queries';
import { sincronizacaoService } from '../services/sincronizacaoService';

const ESPERA_MS = 2000;
const ESPERA_MAX_MS = 120_000;

export function useUltimaSincronizacao(id: string) {
  return useQuery(sincronizacaoQueries.ultima(id));
}

/**
 * Dispara a sincronização. O resultado aparece na linha de status acima da lista, sem toast
 * (spec 28 R20); só erro HTTP vira toast.
 */
export function useSincronizar(id: string) {
  const qc = useQueryClient();
  const { t } = useTranslation(['erros']);
  const { toast } = useToast();
  const [aguardandoOutra, setAguardandoOutra] = useState(false);

  function invalidar() {
    // Spec 20 R6d: a sincronização recarrega as listas do Axiis no servidor; o formulário aberto também.
    qc.removeQueries({
      predicate: ({ queryKey: [a, b] }) =>
        a === 'formulario' && (b === 'paises' || b === 'estados' || b === 'cidades'),
    });
    void qc.invalidateQueries({ queryKey: painelQueries.all(id) });
    void qc.invalidateQueries({ queryKey: campanhasQueries.progresso(id).queryKey });
    // Os totais de convite ficam montados na página inteira (polling da rodada, spec 28 R24).
    void qc.invalidateQueries({ queryKey: convitesQueries.resumo(id).queryKey, exact: true });
    return qc.invalidateQueries({ queryKey: sincronizacaoQueries.all(id) });
  }

  const mutation = useMutation({
    mutationFn: () => sincronizacaoService.sincronizar(id),
    onSuccess: invalidar,
  });

  /** Outra aba sincroniza: espera a `iniciadaEm` da última mudar, por no máximo 120 s (R20). */
  async function esperarOutra() {
    setAguardandoOutra(true);
    const antes = (await sincronizacaoService.ultima(id)).ultima?.iniciadaEm;
    const limite = Date.now() + ESPERA_MAX_MS;
    while (Date.now() < limite) {
      await new Promise((r) => setTimeout(r, ESPERA_MS));
      if ((await sincronizacaoService.ultima(id).catch(() => null))?.ultima?.iniciadaEm !== antes)
        break;
    }
    setAguardandoOutra(false);
    await invalidar();
  }

  /** `onSuccess` só desta chamada: "Carregar…" vai a "Quem recebe", abrir não (spec 28 R16). */
  async function sincronizar(opcoes?: { onSuccess?: () => void }) {
    try {
      await mutation.mutateAsync(undefined, { onSuccess: opcoes?.onSuccess });
    } catch (error) {
      const codigo = mensagemDeErro(error);
      if (codigo === 'SINCRONIZACAO_EM_ANDAMENTO') return esperarOutra();
      if (codigo === 'CAMPANHA_NAO_SINCRONIZAVEL') {
        // A fase mudou (meia-noite, outra aba): a tela se recompõe.
        void qc.invalidateQueries({ queryKey: campanhasQueries.detalhe(id).queryKey });
        void qc.invalidateQueries({ queryKey: campanhasQueries.progresso(id).queryKey });
        return;
      }
      toast.danger({ title: t(`erros:${codigo}`) });
    }
  }

  return { sincronizar, isPending: mutation.isPending || aguardandoOutra };
}
